import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { AdminUserService } from '../services/admin-user.service';
import { firstValueFrom, timeout, catchError, of, take } from 'rxjs';

/**
 * Super Admin Guard
 * Ensures that only users with role 'super_admin' can access the route
 */
export const superAdminGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const adminUserService = inject(AdminUserService);
  const router = inject(Router);

  // Wait for Firebase auth state to initialize (with 2 second timeout)
  let currentUser;
  try {
    currentUser = await firstValueFrom(
      authService.user$.pipe(
        take(1),
        timeout(2000),
        catchError(() => {
          return of(authService.getCurrentUser());
        })
      )
    );
  } catch (error) {
    console.error('Error waiting for auth state:', error);
    currentUser = authService.getCurrentUser();
  }
  
  if (!currentUser) {
    console.log('No authenticated user, redirecting to login');
    return router.createUrlTree(['/login']);
  }

  try {
    // Fetch admin user document by email
    const email = currentUser.email;
    if (!email) {
      console.error('User has no email');
      return router.createUrlTree(['/admin']);
    }

    const adminUsers = await firstValueFrom(
      adminUserService.getUserByEmail(email)
    );

    // Validate admin user document exists
    if (!adminUsers || adminUsers.length === 0) {
      console.log('No admin user document found');
      return router.createUrlTree(['/admin']);
    }

    const adminUser = adminUsers[0];

    // Validate admin user has required fields
    if (!adminUser.status || !adminUser.role) {
      console.error('Admin user document is missing required fields');
      return router.createUrlTree(['/admin']);
    }

    // Validate admin user status is 'active'
    if (adminUser.status !== 'active') {
      console.log('User status is not active:', adminUser.status);
      return router.createUrlTree(['/admin']);
    }

    // Check if user is a super admin
    if (adminUser.role !== 'super_admin') {
      console.log('User is not a super admin, access denied');
      return router.createUrlTree(['/admin']);
    }

    // User is a super admin
    console.log('Super admin access granted');
    return true;
  } catch (error: any) {
    console.error('Error in super admin guard:', error);
    return router.createUrlTree(['/admin']);
  }
};

